// src/theme/BrandSplash.jsx
//
// Tela de boas-vindas exibida logo após o launch screen nativo (Capacitor).
// Fica visível por ~2.5s antes de entrar no app.
//
// Layout:
//   ┌─────────────────────────┐
//   │                         │
//   │     [logo academia]     │  ← grande, centralizado
//   │     Nome da Academia    │
//   │                         │
//   │  ─────────────────────  │
//   │  [⚡] Tecnologia LesVita │  ← fixo, pequeno, não removível
//   └─────────────────────────┘
//
// Quando usar:
//   No AppRoutes.jsx, como fallback do Suspense em plataforma nativa:
//
//   const SuspenseFallback = () => {
//     const { theme, isThemeReady } = useTheme();
//     const isNative = Capacitor.isNativePlatform();
//     if (isNative) return <BrandSplash theme={theme} isReady={isThemeReady} />;
//     return <Loading />;
//   };
//
//   Também pode ser usado como tela de transição pós-login, antes de
//   carregar a PlataformaPessoal — basta controlar via estado.

import React, { useEffect, useState } from "react";
import styles from "../styles/theme/BrandSplash.module.css";
import lesVitaIcon from "../Assets/images/logo/contaEscuro.svg"; // ícone LesVita pequeno

const BrandSplash = ({ theme, isReady = true, onFinish }) => {
  const [visible, setVisible] = useState(true);

  // Trava o scroll enquanto o splash está montado e devolve o app ao topo ao
  // sair. A rota inicial "/" usa overflow:auto (overflow-auto-route), então sem
  // isto um arrasto no splash move o scroll do body e esse offset "vaza" para o
  // dashboard, que aparece subido/descido ao montar.
  useEffect(() => {
    const targets = [document.documentElement, document.body].filter(Boolean);
    const prev = targets.map((el) => el.style.overflow);
    targets.forEach((el) => { el.style.overflow = "hidden"; });
    return () => {
      targets.forEach((el, i) => { el.style.overflow = prev[i]; });
      window.scrollTo(0, 0);
    };
  }, []);

  // Auto-dismiss após 2.5s se onFinish for fornecido
  useEffect(() => {
    if (!onFinish) return;
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onFinish, 300); // aguarda fade out
    }, 2500);
    return () => clearTimeout(timer);
  }, [onFinish]);

  // Cor de fundo: usa splash_bg_image_url se existir, senão splash_bg_color
  const bgStyle = theme?.splash_bg_image_url
    ? {
        backgroundImage: `url(${theme.splash_bg_image_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {
        background: theme?.splash_bg_color
          ? `linear-gradient(180deg, ${theme.background_color ?? theme.splash_bg_color}, ${theme.splash_bg_color})`
          : "linear-gradient(180deg, #00095C, #1428A0)",
      };

  return (
    <div
      className={`${styles.container} ${!visible ? styles.fadeOut : ""}`}
      style={bgStyle}
    >
      {/* Logo e nome da academia */}
      <div className={styles.brandArea}>
        {theme?.logo_url ? (
          <img
            src={theme.logo_url}
            alt={theme?.gym_name ?? "Academia"}
            className={styles.gymLogo}
          />
        ) : (
          // Fallback: inicial da academia em texto grande
          <div className={styles.gymLogoFallback}>
            {(theme?.gym_name ?? "A").charAt(0).toUpperCase()}
          </div>
        )}

        {theme?.gym_name && !theme?.is_default && (
          <p className={styles.gymName}>{theme.gym_name}</p>
        )}
      </div>

      {/* Badge LesVita — FIXO, não configurável pela academia */}
      <div className={styles.poweredBy}>
        <img src={lesVitaIcon} alt="LesVita" className={styles.lesVitaIcon} />
        <span className={styles.poweredByText}>Tecnologia LesVita</span>
      </div>
    </div>
  );
};

export default BrandSplash;
